import * as React from 'react';
import { ApiResponse, StatusCode } from '../../../../utils/api';

/**
 * How long what a tile showed is kept, and shown again without asking, when the dashboard is come back to
 */
export const TileCacheMinutes = 5;

interface CachedTile {
    data: any;
    expiresAt: number;
}

// Kept in memory, so it lasts as long as the page does.  What the API says depends on who is asking and from which service
// center, and signing out and choosing another service center both reload the app, which empties this.
const cache = new Map<string, CachedTile>();

/**
 * Forget everything the tiles kept
 */
export function clearTileCache() {
    cache.clear();
}

const kept = (key: string): CachedTile | undefined => {
    const entry = cache.get(key);

    if (entry && entry.expiresAt > Date.now()) {
        return entry;
    }

    cache.delete(key);
    return undefined;
};

/**
 * What a tile of the dashboard needs to show itself: its data once it has it, whether it could not be had, and a way to ask
 * again.  It asks when the tile is shown, unless what it got the last time is not yet five minutes old, in which case that
 * is what it shows.  What could not be had is not kept, and is asked for again.
 * @param key What the tile is, which tells its data from that of the others
 * @param load Asks for the data
 */
export function useTileData<T>(key: string, load: () => Promise<ApiResponse<T>>) {

    const [data, setData] = React.useState<T | undefined>(() => kept(key)?.data);
    const [failed, setFailed] = React.useState(false);
    const unmounted = React.useRef(false);
    const latestLoad = React.useRef(load);
    latestLoad.current = load;

    // Always asks, and keeps the answer
    const reload = React.useCallback(async () => {
        setFailed(false);
        const response = await latestLoad.current();

        // the answer is worth keeping even when the page was left before it came
        if (response.status === StatusCode.Success) {
            cache.set(key, { data: response.data, expiresAt: Date.now() + TileCacheMinutes * 60 * 1000 });
        }

        if (unmounted.current) {
            return;
        }

        if (response.status === StatusCode.Success) {
            setData(response.data);
        }
        else {
            setFailed(true);
        }
    }, [key]);

    React.useEffect(() => {
        unmounted.current = false;

        if (!kept(key)) {
            reload();
        }

        return () => { unmounted.current = true; };
    }, []);

    return { data, failed, reload };
}
